// src/pages/Reports.tsx
import React, { useContext, useEffect, useMemo, useState } from "react";
import { AuthContext } from "../AuthProvider";
import { useNavigate } from "react-router-dom";
import Spinner from "../components/Spinner";
import { useTranslation } from "react-i18next";
import ErrorPage from "../components/ErrorPage";
import { mapHttpErrorToMessage } from "../utils/mapHttpErrorToMessage";
import http from "../api/apiClient";

// Alleen de utils die we echt gebruiken (allemaal via gedeelde axios-instance met interceptor)
import { fetchWaitingStudents } from "../utils/fetchWaitingStudents";
import { fetchSiblingMetrics } from "../utils/fetchSiblingStats";
import { fetchUnassignedChildren } from "../utils/fetchUnassignedChildren";
import { fetchNewsletterCounts } from "../utils/fetchNewsletterCounts";
import { fetchWaitingWithClass } from "../utils/fetchWaitingWithClass";
import { useCan } from "../authz/useCan";
import {
  LdsOverviewGrid,
  LdsOverviewHero,
  LdsOverviewPage,
} from "../components/lds/LdsOverview";
import {
  FaUserShield,
  FaHourglassHalf,
  FaUserFriends,
  FaClipboardList,
  FaEnvelopeOpenText,
  FaFileExport,
  FaSyncAlt,
  FaTable,
  FaUserPlus,
  FaUsers,
} from "react-icons/fa";
import { FaChartLine } from "react-icons/fa6";

const parseDateTime = (value) => {
  if (!value) return null;
  const raw = String(value).trim();
  if (!raw) return null;

  // Support dd-mm-yyyy (optionally with time)
  const dmyMatch = raw.match(/^(\d{2})-(\d{2})-(\d{4})(?:[,\sT]+(\d{2}):(\d{2})(?::(\d{2}))?)?/);
  if (dmyMatch) {
    const [, dd, mm, yyyy, hh = "00", min = "00", ss = "00"] = dmyMatch;
    const normalized = `${yyyy}-${mm}-${dd}T${hh}:${min}:${ss}`;
    const d = new Date(normalized);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  // Numeric timestamp (seconds or milliseconds), optionally with decimals.
  if (/^\d+(?:\.\d+)?$/.test(raw)) {
    const numeric = Number(raw);
    if (!Number.isFinite(numeric)) return null;
    const ms = numeric > 1e12 ? numeric : numeric * 1000;
    const d = new Date(ms);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  let normalized = raw;
  if (normalized.includes(" ") && !normalized.includes("T")) {
    normalized = normalized.replace(" ", "T");
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(normalized)) {
    normalized = `${normalized}T00:00:00`;
  }
  const d = new Date(normalized);
  return Number.isNaN(d.getTime()) ? null : d;
};

const formatShortDateTime = (value) => {
  const d = parseDateTime(value);
  if (!d) return null;

  const raw = value == null ? "" : String(value).trim();
  const hasTimezone = /Z$|[+-]\d{2}:\d{2}$/.test(raw);
  const useUTC = hasTimezone;

  const pad2 = (n) => String(n).padStart(2, "0");
  const yyyy = useUTC ? d.getUTCFullYear() : d.getFullYear();
  const mm = pad2((useUTC ? d.getUTCMonth() : d.getMonth()) + 1);
  const dd = pad2(useUTC ? d.getUTCDate() : d.getDate());
  const hh = pad2(useUTC ? d.getUTCHours() : d.getHours());
  const min = pad2(useUTC ? d.getUTCMinutes() : d.getMinutes());

  return `${dd}-${mm}-${yyyy}, ${hh}:${min}`;
};

const extractTimestampFromText = (text) => {
  if (!text) return null;
  const raw = String(text);
  const match =
    raw.match(/\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z/) ||
    raw.match(/\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}/) ||
    raw.match(/\d{2}-\d{2}-\d{4}(?:[,\s]+\d{2}:\d{2}(?::\d{2})?)?/) ||
    raw.match(/\b\d{10,13}(?:\.\d+)?\b/) ||
    raw.match(/\d{4}-\d{2}-\d{2}/);
  return match?.[0] ?? null;
};

const Reports = () => {
  const { keycloak } = useContext(AuthContext);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const canListWaiting = useCan("school.waiting-list", "list");
  const canViewWaiting = useCan("school.waiting-list", "view");
  const canAssignWaiting = useCan("school.waiting-list", "assign");
  const canSeeWaiting = canListWaiting || canViewWaiting || canAssignWaiting;
  const canReadChildren = useCan("family.children", "read");
  const canListChildren = useCan("family.children", "list");
  const canSeeSiblings = useCan("reports.root", "view");
  const canViewFamilyReports = useCan("reports.families", "view");
  const canViewNewFamiliesReport = useCan("reports.new-families", "view");
  const canUseReportBuilder = useCan("reports.builder", "view");
  const canSeeUnassigned = useCan("signals.unassigned-children", "view");
  const canManageDuoRegistrations = useCan("reports.duo-registrations", "manage");
  const canSeeWaitingWithClass = canSeeWaiting && (canReadChildren || canListChildren);
  const canViewAttendanceReports = useCan("reports.attendance", "view");
  const canViewOutingsReport = useCan("reports.outings", "view");
  const attendanceFeatureEnabled = useCan("school.attendance", "view");
  const canSeeNewsletter = useCan("newsletter.subscriptions", "read");
  const canViewPrivacy = useCan("reports.privacy", "view");
  const canViewPrivacyQuickStats = useCan("reports.privacy", "view");
  const canViewAlumni = false;
  const canViewAnalytics = useCan("admin.analytics", "read");
  const canViewMailchimpSync = useCan("newsletter.recipients", "status");
  const canExportMailchimpSync = useCan("newsletter.recipients", "export");
  const canSyncMailchimpSync = useCan("newsletter.recipients", "sync");
  const canForceMailchimpSync = canExportMailchimpSync && canSyncMailchimpSync;

  // State
  const [waitingCount, setWaitingCount] = useState(0);
  const [siblingMetrics, setSiblingMetrics] = useState({
    householdsWithSiblings: 0,
    totalSiblingPairs: 0,
    totalChildrenWithSiblings: 0,
    averageChildrenPerSiblingFamily: 0,
    largestSiblingGroup: 0,
  });
  const [unassignedChildren, setUnassignedChildren] = useState([]);
  const [waitingWithClassCount, setWaitingWithClassCount] = useState(0);
  const [duoPendingCount, setDuoPendingCount] = useState(0);
  const [newsletterStats, setNewsletterStats] = useState({ subscribed: 0, optedOut: 0 });
  const [newsletterRefreshing, setNewsletterRefreshing] = useState(false);
  const [newsletterFeedback, setNewsletterFeedback] = useState(null);
  const [parentExportAudience, setParentExportAudience] = useState("all");
  const [parentExportLoading, setParentExportLoading] = useState(false);
  const [parentExportError, setParentExportError] = useState(null);
  const [privacyTotals, setPrivacyTotals] = useState({ total: 0, withConsent: 0 });
  const [alumniPrefs, setAlumniPrefs] = useState([]);
  const [alumniLoading, setAlumniLoading] = useState(false);
  const [alumniError, setAlumniError] = useState(null);
  const [alumniExpanded, setAlumniExpanded] = useState(false);
  const [mailchimpSyncStatus, setMailchimpSyncStatus] = useState({
    ok: null,
    upserted: null,
    message: null,
    lastSyncedAt: null,
    neverSynced: false,
    error: null,
    trigger: null,
  });
  const [mailchimpSyncing, setMailchimpSyncing] = useState(false);
  const [mailchimpSyncFeedback, setMailchimpSyncFeedback] = useState(null);

  const mailchimpLastLabel = useMemo(() => {
    const candidate =
      mailchimpSyncStatus.lastSyncedAt ||
      extractTimestampFromText(mailchimpSyncStatus.message) ||
      null;
    if (!candidate) return null;
    return formatShortDateTime(candidate) || String(candidate);
  }, [mailchimpSyncStatus.lastSyncedAt, mailchimpSyncStatus.message]);

  const mailchimpMessageWithoutTimestamp = useMemo(() => {
    if (!mailchimpSyncStatus.message) return null;
    const message = String(mailchimpSyncStatus.message);
    const ts = extractTimestampFromText(message);
    if (!ts) return message;
    const stripped = message.replace(ts, "").replace(/^synced at\s*/i, "").trim();
    const cleaned = stripped.replace(/^[:,-]\s*/g, "").trim();
    return cleaned || null;
  }, [mailchimpSyncStatus.message]);

  const mailchimpPrettyMessage = useMemo(() => {
    if (!mailchimpSyncStatus.message) return null;
    const message = String(mailchimpSyncStatus.message);
    const ts = extractTimestampFromText(message);
    if (!ts) return message;
    const pretty = formatShortDateTime(ts);
    if (!pretty) return message;
    return message.replace(ts, pretty);
  }, [mailchimpSyncStatus.message]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null); // { message, status }
  const averageChildrenPerSiblingFamily = Number(
    siblingMetrics.averageChildrenPerSiblingFamily || 0
  ).toFixed(1);
  const handleExportAlumni = async () => {
    if (!canViewAlumni || alumniLoading) return;
    setAlumniLoading(true);
    setAlumniError(null);
    try {
      const res = await http.get("/alumni/export", { responseType: "blob" });
      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "alumni-preferences.csv");
        document.body.appendChild(link);
        link.click();
        link.remove();
      } catch (err) {
        const mapped = mapHttpErrorToMessage(err, t);
      const text =
        typeof mapped === "string"
          ? mapped
          : mapped?.message || mapped?.title || t("reports.alumni.export_failed", {
              defaultValue: "Export failed",
            });
      setAlumniError(text);
    } finally {
      setAlumniLoading(false);
    }
  };

  const yesNo = (val) =>
    val ? t("common.yes", { defaultValue: "Yes" }) : t("common.no", { defaultValue: "No" });

  const handleRecomputeNewsletterEligibility = async () => {
    if (!canSeeNewsletter || newsletterRefreshing) return;
    setNewsletterFeedback(null);
    setNewsletterRefreshing(true);
    try {
      await http.post("/admin/newsletter/recompute/all");
      try {
        const refreshed = await fetchNewsletterCounts();
        setNewsletterStats(refreshed);
      } catch (fetchErr) {
        // als herscannen lukt maar ophalen faalt, meld fout maar laat backend-run niet falen
        const mapped = mapHttpErrorToMessage(fetchErr, t);
        const text =
          typeof mapped === "string"
            ? mapped
            : mapped?.message || mapped?.title || t("reports.newsletter_recompute_failed");
        setNewsletterFeedback({ type: "error", message: text });
        return;
      }
      setNewsletterFeedback({
        type: "success",
        message: t("reports.newsletter_recompute_success"),
      });
    } catch (err) {
      const mapped = mapHttpErrorToMessage(err, t);
      const text =
        typeof mapped === "string"
          ? mapped
          : mapped?.message || mapped?.title || t("reports.newsletter_recompute_failed");
      setNewsletterFeedback({ type: "error", message: text });
    } finally {
      setNewsletterRefreshing(false);
    }
  };

  const refreshMailchimpStatus = async () => {
    if (!canViewMailchimpSync) return;
    try {
      const res = await http.get("/admin/newsletter/recipients/mailchimp/status", {
        validateStatus: (code) => (code >= 200 && code < 300) || code === 204,
      });
      if (res?.status === 204) {
        setMailchimpSyncStatus({
          ok: null,
          upserted: null,
          message: null,
          lastSyncedAt: null,
          neverSynced: true,
          error: null,
          trigger: null,
        });
        return;
      }
      const data = res?.data || {};
      const inferredTs = data?.lastSyncedAt || extractTimestampFromText(data?.message) || null;
      setMailchimpSyncStatus({
        ok: typeof data?.ok === "boolean" ? data.ok : null,
        upserted: typeof data?.upserted === "number" ? data.upserted : null,
        message: typeof data?.message === "string" ? data.message : null,
        lastSyncedAt: inferredTs,
        neverSynced: false,
        error: null,
        trigger: typeof data?.trigger === "string" ? data.trigger : typeof data?.reason === "string" ? data.reason : null,
      });
    } catch {
      setMailchimpSyncStatus({
        ok: null,
        upserted: null,
        message: null,
        lastSyncedAt: null,
        neverSynced: false,
        error: true,
        trigger: null,
      });
    }
  };

  const handleForceMailchimpSync = async () => {
    if (!canForceMailchimpSync || mailchimpSyncing) return;
    setMailchimpSyncFeedback(null);
    setMailchimpSyncing(true);
    try {
      const res = await http.get("/admin/newsletter/recipients/export", {
        params: { syncMailchimp: "true" },
        responseType: "blob",
      });
      const contentType = String(res?.headers?.["content-type"] || "").toLowerCase();
      const blob = res?.data;
      if (contentType.includes("application/json") && blob?.text) {
        const text = await blob.text();
        try {
          const data = text ? JSON.parse(text) : {};
          if (data?.message) setMailchimpSyncFeedback(String(data.message));
        } catch {
          // ignore
        }
      } else {
        setMailchimpSyncFeedback(
          t("reports.mailchimp_sync.forced", { defaultValue: "Sync has been triggered." })
        );
      }
      await refreshMailchimpStatus();
    } catch (err) {
      const mapped = mapHttpErrorToMessage(err, t);
      const text =
        typeof mapped === "string"
          ? mapped
          : mapped?.message || mapped?.title || t("common.error", { defaultValue: "Error" });
      setMailchimpSyncFeedback(text);
    } finally {
      setMailchimpSyncing(false);
    }
  };

  // Alle rapportdata laden
  useEffect(() => {
    let active = true;

    const loadReports = async () => {
      if (!keycloak) return;
      const wantsData =
        canSeeWaiting ||
        canSeeSiblings ||
        canSeeUnassigned ||
        canSeeWaitingWithClass ||
        canSeeNewsletter ||
        canViewPrivacyQuickStats ||
        canViewAlumni ||
        canViewMailchimpSync ||
        canManageDuoRegistrations;

      if (!wantsData) {
        setWaitingCount(0);
        setSiblingMetrics({
          householdsWithSiblings: 0,
          totalSiblingPairs: 0,
          totalChildrenWithSiblings: 0,
          averageChildrenPerSiblingFamily: 0,
          largestSiblingGroup: 0,
        });
        setUnassignedChildren([]);
        setNewsletterStats({ subscribed: 0, optedOut: 0 });
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);
      try {
        const waitingPromise = canSeeWaiting ? fetchWaitingStudents() : Promise.resolve(null);
        const siblingsPromise = canSeeSiblings ? fetchSiblingMetrics() : Promise.resolve(null);
        const unassignedPromise = canSeeUnassigned ? fetchUnassignedChildren() : Promise.resolve(null);
        const waitingWithClassPromise = canSeeWaitingWithClass ? fetchWaitingWithClass() : Promise.resolve(null);
        const newsletterPromise = canSeeNewsletter ? fetchNewsletterCounts() : Promise.resolve(null);
        const mailchimpPromise = canViewMailchimpSync
          ? http.get("/admin/newsletter/recipients/mailchimp/status", {
              validateStatus: (code) => (code >= 200 && code < 300) || code === 204,
            })
          : Promise.resolve(null);
        const privacyPromise = canViewPrivacyQuickStats
          ? http.get("/reports/privacy/summary").catch(() => null)
          : Promise.resolve(null);
        const alumniPromise = canViewAlumni ? http.get("/alumni/all").catch(() => null) : Promise.resolve(null);
        const duoPromise = canManageDuoRegistrations
          ? http.get("/reports/duo-registrations?completed=false")
          : Promise.resolve(null);

        const [rWaiting, rSiblings, rUnassigned, rWaitingWithClass, rNewsletter, rMailchimp, rPrivacy, rAlumni, rDuo] =
          await Promise.allSettled([
            waitingPromise,
            siblingsPromise,
            unassignedPromise,
            waitingWithClassPromise,
            newsletterPromise,
            mailchimpPromise,
            privacyPromise,
            alumniPromise,
            duoPromise,
          ]);

        if (!active) return;

        if (rWaiting.status === "fulfilled" && rWaiting.value != null) {
          setWaitingCount(rWaiting.value ?? 0);
        } else if (!canSeeWaiting || rWaiting.status === "rejected") {
          setWaitingCount(0);
        }

        if (rSiblings.status === "fulfilled" && rSiblings.value != null) {
          setSiblingMetrics(rSiblings.value);
        } else if (!canSeeSiblings || rSiblings.status === "rejected") {
          setSiblingMetrics({
            householdsWithSiblings: 0,
            totalSiblingPairs: 0,
            totalChildrenWithSiblings: 0,
            averageChildrenPerSiblingFamily: 0,
            largestSiblingGroup: 0,
          });
        }

        if (rUnassigned.status === "fulfilled" && rUnassigned.value != null) {
          setUnassignedChildren(Array.isArray(rUnassigned.value) ? rUnassigned.value : []);
        } else if (!canSeeUnassigned || rUnassigned.status === "rejected") {
          setUnassignedChildren([]);
        }

        if (rDuo.status === "fulfilled" && rDuo.value?.data != null) {
          setDuoPendingCount(Array.isArray(rDuo.value.data) ? rDuo.value.data.length : 0);
        } else {
          setDuoPendingCount(0);
        }

        if (rNewsletter.status === "fulfilled" && rNewsletter.value != null) {
          setNewsletterStats({
            subscribed: rNewsletter.value?.subscribed ?? 0,
            optedOut: rNewsletter.value?.optedOut ?? 0,
          });
        } else if (!canSeeNewsletter || rNewsletter.status === "rejected") {
          setNewsletterStats({ subscribed: 0, optedOut: 0 });
        }

        if (canViewMailchimpSync) {
          if (rMailchimp.status === "fulfilled") {
            const res = rMailchimp.value;
            if (res?.status === 204) {
              setMailchimpSyncStatus({
                ok: null,
                upserted: null,
                message: null,
                lastSyncedAt: null,
                neverSynced: true,
                error: null,
                trigger: null,
              });
            } else {
              const data = res?.data || {};
              const inferredTs = data?.lastSyncedAt || extractTimestampFromText(data?.message) || null;
              setMailchimpSyncStatus({
                ok: typeof data?.ok === "boolean" ? data.ok : null,
                upserted: typeof data?.upserted === "number" ? data.upserted : null,
                message: typeof data?.message === "string" ? data.message : null,
                lastSyncedAt: inferredTs,
                neverSynced: false,
                error: null,
                trigger: typeof data?.trigger === "string" ? data.trigger : typeof data?.reason === "string" ? data.reason : null,
              });
            }
          } else {
            setMailchimpSyncStatus({
              ok: null,
              upserted: null,
              message: null,
              lastSyncedAt: null,
              neverSynced: false,
              error: true,
              trigger: null,
            });
          }
        }

        if (rWaitingWithClass.status === "fulfilled" && rWaitingWithClass.value != null) {
          setWaitingWithClassCount(
            Array.isArray(rWaitingWithClass.value) ? rWaitingWithClass.value.length : Number(rWaitingWithClass.value || 0)
          );
        } else if (!canSeeWaitingWithClass || rWaitingWithClass.status === "rejected") {
          setWaitingWithClassCount(0);
        }

        if (rPrivacy.status === "fulfilled" && rPrivacy.value?.data) {
          const summary = rPrivacy.value.data;
          setPrivacyTotals({
            total: summary?.totalChildren ?? summary?.total ?? 0,
            withConsent: summary?.withConsent ?? summary?.completed ?? 0,
          });
        } else if (!canViewPrivacyQuickStats || rPrivacy.status === "rejected") {
          setPrivacyTotals({ total: 0, withConsent: 0 });
        }

        if (rAlumni.status === "fulfilled" && rAlumni.value?.data) {
          const data = rAlumni.value.data;
          const list = Array.isArray(data)
            ? data
            : Array.isArray(data?.content)
            ? data.content
            : Array.isArray(data?.items)
            ? data.items
            : [];
          setAlumniPrefs(list);
          setAlumniError(null);
        } else if (rAlumni.status === "rejected") {
          setAlumniPrefs([]);
          const mapped = mapHttpErrorToMessage(rAlumni.reason, t);
          setAlumniError(typeof mapped === "string" ? mapped : mapped?.message || mapped?.title || null);
        } else {
          setAlumniPrefs([]);
        }
      } catch (err) {
        if (!active) return;
        setError({
          message: mapHttpErrorToMessage(err, t),
          status: err?.response?.status || 500,
        });
      } finally {
        if (active) setLoading(false);
      }
    };

    loadReports();
    return () => {
      active = false;
    };
  }, [keycloak, t, canSeeWaiting, canSeeSiblings, canSeeUnassigned, canSeeWaitingWithClass, canSeeNewsletter, canViewAlumni, canViewMailchimpSync, canViewPrivacyQuickStats, canManageDuoRegistrations]);

  // Render
  if (loading) return <Spinner />;
  if (error) {
    return (
      <ErrorPage
        message={typeof error === "string" ? error : error.message}
        status={typeof error === "string" ? undefined : error.status}
        onRetry={() => window.location.reload()}
      />
    );
  }

  return (
    <LdsOverviewPage>
      <LdsOverviewHero
        icon={FaChartLine}
        eyebrow={t("reports.section_label", {
          defaultValue: "Administration",
        })}
        title={t("reports.title", {
          defaultValue: "Reports",
        })}
        description={t("reports.subtitle", {
          defaultValue:
            "Review operational insights, exports and administrative statistics.",
        })}
      />

      <LdsOverviewGrid className="sm:grid-cols-2 xl:grid-cols-3">

        {canViewFamilyReports && (
          <button
            type="button"
            className="group rounded-[24px] border border-slate-200 bg-white p-5 text-left shadow-sm transition hover:-translate-y-0.5 hover:border-indigo-200 hover:shadow-lg dark:border-white/10 dark:bg-slate-900/80 dark:hover:border-indigo-400/30"
            onClick={() => navigate("/reports/families")}
          >
            <h2 className="text-lg font-bold text-slate-950 dark:text-white">
              <span className="inline-flex items-center gap-2">
                <FaUsers className="text-2xl text-indigo-600 transition duration-200 group-hover:scale-110 dark:text-indigo-300" />
                {t("reports.families.title", { defaultValue: "Family lists" })}
              </span>
            </h2>
            <p className="mt-3 text-sm text-slate-600 dark:text-slate-300">
              {t("reports.families.card_hint", {
                defaultValue: "Filter, compose, print and copy family lists.",
              })}
            </p>
          </button>
        )}

        {canUseReportBuilder && (
          <button
            type="button"
            className="group rounded-[24px] border border-slate-200 bg-white p-5 text-left shadow-sm transition hover:-translate-y-0.5 hover:border-indigo-200 hover:shadow-lg dark:border-white/10 dark:bg-slate-900/80 dark:hover:border-indigo-400/30"
            onClick={() => navigate("/reports/builder")}
          >
            <h2 className="text-lg font-bold text-slate-950 dark:text-white">
              <span className="inline-flex items-center gap-2">
                <FaTable className="text-2xl text-indigo-600 transition duration-200 group-hover:scale-110 dark:text-indigo-300" />
                {t("reports.builder.card_title", { defaultValue: "Make a report" })}
              </span>
            </h2>
            <p className="mt-3 text-sm text-slate-600 dark:text-slate-300">
              {t("reports.builder.card_hint", {
                defaultValue: "Compose your own report from families, parents, children and classes.",
              })}
            </p>
          </button>
        )}

        {canViewNewFamiliesReport && (
          <button
            type="button"
            className="group rounded-[24px] border border-slate-200 bg-white p-5 text-left shadow-sm transition hover:-translate-y-0.5 hover:border-indigo-200 hover:shadow-lg dark:border-white/10 dark:bg-slate-900/80 dark:hover:border-indigo-400/30"
            onClick={() => navigate("/reports/new-families")}
          >
            <h2 className="text-lg font-bold text-slate-950 dark:text-white">
              <span className="inline-flex items-center gap-2">
                <FaUserPlus className="text-2xl text-indigo-600 transition duration-200 group-hover:scale-110 dark:text-indigo-300" />
                {t("reports.new_families.title", { defaultValue: "New families" })}
              </span>
            </h2>
            <p className="mt-3 text-sm text-slate-600 dark:text-slate-300">
              {t("reports.new_families.card_hint", {
                defaultValue: "View families that were recently added.",
              })}
            </p>
          </button>
        )}


        {/* Waiting Students */}
        {/* Waiting Students */}
        {canSeeWaiting && (
          <div
            className="group cursor-pointer rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:border-indigo-200 hover:shadow-lg dark:border-white/10 dark:bg-slate-900/80 dark:hover:border-indigo-400/30"
            onClick={() => navigate("/school/waiting-list")}
          >
            <h2 className="text-lg font-bold text-slate-950 dark:text-white">
              <span className="inline-flex items-center gap-2">
                <FaHourglassHalf className="text-2xl text-indigo-600 transition duration-200 group-hover:scale-110 dark:text-indigo-300" />
                {t("reports.waiting_students")}
              </span>
            </h2>
            <p className="mt-2 text-4xl font-bold text-indigo-600 dark:text-indigo-300">{waitingCount}</p>
          </div>
        )}

        {/* Attendance Report */}
        {attendanceFeatureEnabled && canViewAttendanceReports && (
          <div
            className="group cursor-pointer rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:border-indigo-200 hover:shadow-lg dark:border-white/10 dark:bg-slate-900/80 dark:hover:border-indigo-400/30"
            onClick={() => navigate("/reports/attendance")}
          >
            <h2 className="text-lg font-bold text-slate-950 dark:text-white">
              <span className="inline-flex items-center gap-2">
                <FaChartLine className="text-2xl text-indigo-600 transition duration-200 group-hover:scale-110 dark:text-indigo-300" />
                {t("reports.attendance.card_title", { defaultValue: "Attendance by classroom" })}
              </span>
            </h2>
            <p className="mt-3 text-sm text-slate-600 dark:text-slate-300">
              {t("reports.attendance.card_subtitle", {
                defaultValue: "Review attendance, absence and late-arrival trends by classroom or across the school.",
              })}
            </p>
          </div>
        )}

        {canViewOutingsReport && (
          <div
            className="group cursor-pointer rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:border-indigo-200 hover:shadow-lg dark:border-white/10 dark:bg-slate-900/80 dark:hover:border-indigo-400/30"
            onClick={() => navigate("/reports/outings")}
          >
            <h2 className="text-lg font-bold text-slate-950 dark:text-white">
              <span className="inline-flex items-center gap-2">
                <FaClipboardList className="text-2xl text-indigo-600 transition duration-200 group-hover:scale-110 dark:text-indigo-300" />
                {t("reports.outings.card_title", { defaultValue: "Outings" })}
              </span>
            </h2>
            <p className="mt-3 text-sm text-slate-600 dark:text-slate-300">
              {t("reports.outings.card_subtitle", {
                defaultValue: "Review outings by classroom, response totals and missing submissions.",
              })}
            </p>
          </div>
        )}

        {/* Sibling Stats */}
        {canSeeSiblings && (
          <div className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-slate-900/80">
            <h2 className="text-lg font-bold text-slate-950 dark:text-white">
              <span className="inline-flex items-center gap-2">
                <FaUserFriends className="text-2xl text-indigo-600 dark:text-indigo-300" />
                {t("reports.sibling_statistics")}
              </span>
            </h2>
            <p className="mt-2 text-4xl font-bold text-indigo-600 dark:text-indigo-300">
              {siblingMetrics.householdsWithSiblings}
            </p>
            <p className="text-xs font-bold uppercase tracking-[0.08em] text-slate-500 dark:text-slate-400">
              {t("reports.siblings.households")}
            </p>
            <div className="mt-4 grid grid-cols-2 gap-x-4 gap-y-3 text-sm text-slate-600 dark:text-slate-300">
              <div>
                <p className="font-semibold text-gray-800 dark:text-gray-100">
                  {siblingMetrics.totalChildrenWithSiblings}
                </p>
                <p className="text-xs font-bold uppercase tracking-[0.08em] text-slate-500 dark:text-slate-400">
                  {t("reports.siblings.children")}
                </p>
              </div>
              <div>
                <p className="font-semibold text-gray-800 dark:text-gray-100">
                  {siblingMetrics.totalSiblingPairs}
                </p>
                <p className="text-xs font-bold uppercase tracking-[0.08em] text-slate-500 dark:text-slate-400">
                  {t("reports.siblings.pairs")}
                </p>
              </div>
              <div>
                <p className="font-semibold text-gray-800 dark:text-gray-100">
                  {averageChildrenPerSiblingFamily}
                </p>
                <p className="text-xs font-bold uppercase tracking-[0.08em] text-slate-500 dark:text-slate-400">
                  {t("reports.siblings.average_per_family")}
                </p>
              </div>
              <div>
                <p className="font-semibold text-gray-800 dark:text-gray-100">
                  {siblingMetrics.largestSiblingGroup}
                </p>
                <p className="text-xs font-bold uppercase tracking-[0.08em] text-slate-500 dark:text-slate-400">
                  {t("reports.siblings.largest_group")}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Newsletter subscribers */}
        {canSeeNewsletter && (
          <div
            role="button"
            tabIndex={0}
            onClick={() => handleRecomputeNewsletterEligibility()}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                handleRecomputeNewsletterEligibility();
              }
            }}
            aria-disabled={newsletterRefreshing}
            className={`rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-slate-900/80 transition focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:ring-offset-2 dark:focus:ring-offset-gray-900 ${
              newsletterRefreshing ? "opacity-60 cursor-wait" : "cursor-pointer hover:-translate-y-0.5 hover:shadow-[0_25px_50px_rgba(15,23,42,0.15)] dark:hover:shadow-[0_25px_50px_rgba(0,0,0,0.6)]"
            }`}
          >
            <h2 className="text-lg font-bold text-slate-950 dark:text-white">
              <span className="inline-flex items-center gap-2">
                <FaEnvelopeOpenText className="text-2xl text-indigo-600 dark:text-indigo-300" />
                {t("reports.newsletter_recipients")}
              </span>
            </h2>
            <div className="mt-2 grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.08em] text-slate-500 dark:text-slate-400">
                  {t("reports.newsletter_subscribed")}
                </p>
                <p className="text-3xl font-bold text-indigo-600 dark:text-indigo-300">
                  {newsletterStats.subscribed}
                </p>
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.08em] text-slate-500 dark:text-slate-400">
                  {t("reports.newsletter_opted_out")}
                </p>
                <p className="text-3xl font-bold text-amber-600 dark:text-amber-300">
                  {newsletterStats.optedOut}
                </p>
              </div>
            </div>
            <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
              {newsletterRefreshing
                ? t("reports.newsletter_recompute_in_progress", { defaultValue: "Recomputing eligibility…" })
                : t("reports.newsletter_recompute_hint", {
                    defaultValue: "Click to recompute newsletter eligibility for all parents.",
                  })}
            </p>
          </div>
        )}

	        {/* Mailchimp sync info */}
	        {canViewMailchimpSync && (
	          <div className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-slate-900/80">
            <div className="flex items-start justify-between gap-3">
              <h2 className="text-lg font-bold text-slate-950 dark:text-white">
                <span className="inline-flex items-center gap-2">
                  <FaSyncAlt className="text-2xl text-indigo-600 dark:text-indigo-300" />
                  {t("reports.mailchimp_sync.title", { defaultValue: "Mailchimp sync" })}
                </span>
              </h2>

              {mailchimpSyncStatus.neverSynced ? (
                <span className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-gray-50 px-3 py-1 text-xs font-semibold text-gray-800 dark:border-gray-700 dark:bg-gray-900/40 dark:text-gray-100">
                  {t("reports.mailchimp_sync.never", { defaultValue: "Never" })}
                </span>
              ) : mailchimpSyncStatus.ok === true ? (
                <span className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-800 dark:border-emerald-500/40 dark:bg-emerald-900/25 dark:text-emerald-100">
                  <span className="inline-flex animate-spin" style={{ animationDuration: "2.6s" }}>
                    <FaSyncAlt />
                  </span>
                  {t("common.ok", { defaultValue: "OK" })}
                </span>
              ) : mailchimpSyncStatus.ok === false ? (
                <span className="inline-flex items-center gap-2 rounded-full border border-red-200 bg-red-50 px-3 py-1 text-xs font-semibold text-red-800 dark:border-red-500/40 dark:bg-red-900/25 dark:text-red-100">
                  {t("common.error", { defaultValue: "Error" })}
                </span>
              ) : (
                <span className="inline-flex items-center gap-2 rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-900 dark:border-amber-500/40 dark:bg-amber-900/25 dark:text-amber-100">
                  {t("common.unknown", { defaultValue: "Unknown" })}
                </span>
              )}
	            </div>

	            <div className="mt-3 grid grid-cols-2 gap-4">
	              <div>
	                <p className="text-xs font-bold uppercase tracking-[0.08em] text-slate-500 dark:text-slate-400">
	                  {t("reports.mailchimp_sync.upserted", { defaultValue: "Upserted" })}
	                </p>
	                <p className="text-3xl font-bold text-indigo-600 dark:text-indigo-300">
	                  {typeof mailchimpSyncStatus.upserted === "number" ? mailchimpSyncStatus.upserted : "—"}
	                </p>
	              </div>
		              <div>
		                <p className="text-xs font-bold uppercase tracking-[0.08em] text-slate-500 dark:text-slate-400">
		                  {t("reports.mailchimp_sync.last", { defaultValue: "Last sync" })}
		                </p>
		                <p className="text-sm font-semibold text-gray-800 dark:text-gray-100">
		                  {mailchimpLastLabel || "—"}
		                </p>
		                {mailchimpMessageWithoutTimestamp && (
		                  <p className="mt-2 text-xs text-gray-500 dark:text-gray-400 break-words">
		                    {mailchimpMessageWithoutTimestamp}
		                  </p>
		                )}
		                {mailchimpSyncStatus.trigger && (
		                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 break-words">
		                    {t("reports.mailchimp_sync.trigger", { defaultValue: "Trigger" })}: {mailchimpSyncStatus.trigger}
		                  </p>
		                )}
		              </div>
		            </div>
	
		            {canForceMailchimpSync && (
		              <div className="mt-4 flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={handleForceMailchimpSync}
                  disabled={mailchimpSyncing}
                  className="inline-flex items-center gap-2 rounded-full border border-indigo-200 bg-indigo-50 px-4 py-2 text-sm font-bold text-indigo-700 shadow-sm transition hover:border-indigo-300 hover:bg-indigo-100 disabled:cursor-not-allowed disabled:opacity-60 dark:border-indigo-400/30 dark:bg-indigo-400/10 dark:text-indigo-200 dark:hover:bg-indigo-400/20"
                >
                  <FaSyncAlt />
                  {mailchimpSyncing
                    ? t("common.loading", { defaultValue: "Working..." })
                    : t("reports.mailchimp_sync.force", { defaultValue: "Force sync" })}
                </button>
                {mailchimpSyncFeedback && (
                  <span className="text-xs text-gray-500 dark:text-gray-400 break-words">
                    {mailchimpSyncFeedback}
                  </span>
	                )}
	              </div>
	            )}
          </div>
        )}

        {/* Analytics */}
        {canViewAnalytics && (
          <div
            className="group cursor-pointer rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:border-indigo-200 hover:shadow-lg dark:border-white/10 dark:bg-slate-900/80 dark:hover:border-indigo-400/30"
            onClick={() => navigate("/reports/analytics")}
          >
            <h2 className="text-lg font-bold text-slate-950 dark:text-white">
              <span className="inline-flex items-center gap-2">
                <FaChartLine className="text-2xl text-indigo-600 transition duration-200 group-hover:scale-110 dark:text-indigo-300" />
                {t("reports.analytics.title", { defaultValue: "Analytics" })}
              </span>
            </h2>
            <p className="mt-3 text-sm text-slate-600 dark:text-slate-300">
              {t("reports.analytics.card_hint", {
                defaultValue: "Review top pages, features and daily activity.",
              })}
            </p>
          </div>
        )}

        {canManageDuoRegistrations && (
          <div
            className="group cursor-pointer rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:border-indigo-200 hover:shadow-lg dark:border-white/10 dark:bg-slate-900/80 dark:hover:border-indigo-400/30"
            onClick={() => navigate("/reports/duo-registrations")}
          >
            <h2 className="text-lg font-bold text-slate-950 dark:text-white">
              <span className="inline-flex items-center gap-2">
                <FaClipboardList className="text-2xl text-indigo-600 dark:text-indigo-300" />
                {t("duo.title", { defaultValue: "DUO registrations" })}
              </span>
            </h2>
            <p className={`mt-2 text-4xl font-bold ${duoPendingCount > 0 ? "text-amber-500 dark:text-amber-300" : "text-indigo-600 dark:text-indigo-300"}`}>
              {duoPendingCount}
            </p>
            <p className="mt-3 text-sm text-slate-600 dark:text-slate-300">
              {t("duo.report_hint", { defaultValue: "View pupils who are ready for DUO registration." })}
            </p>
          </div>
        )}

        {/* Privacy consents */}
        {canViewPrivacy && (
          <div
            className="group cursor-pointer rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:border-indigo-200 hover:shadow-lg dark:border-white/10 dark:bg-slate-900/80 dark:hover:border-indigo-400/30"
            onClick={() => navigate("/reports/privacy")}
          >
            <h2 className="text-lg font-bold text-slate-950 dark:text-white">
              <span className="inline-flex items-center gap-3">
                <FaUserShield className="text-2xl text-indigo-600 transition duration-200 group-hover:scale-110 dark:text-indigo-300" />
                {t("reports.privacy.title")}
              </span>
            </h2>
            <p className="mt-2 text-4xl font-bold text-indigo-600 dark:text-indigo-300">
              {privacyTotals.withConsent}
              <span className="ml-2 text-base text-gray-500 dark:text-gray-400">
                / {privacyTotals.total}
              </span>
            </p>
            <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
              {t("reports.privacy.tile_hint", {
                defaultValue: "Children with privacy consent",
              })}
            </p>
          </div>
        )}

        {/* Waiting list reports */}
        {canSeeUnassigned && (
          <div
            className="group cursor-pointer rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:border-indigo-200 hover:shadow-lg dark:border-white/10 dark:bg-slate-900/80 dark:hover:border-indigo-400/30"
            onClick={() => navigate("/reports/unassigned-children")}
          >
            <h2 className="text-lg font-bold text-slate-950 dark:text-white">
              <span className="inline-flex items-center gap-2">
                <FaClipboardList className="text-2xl text-indigo-600 transition duration-200 group-hover:scale-110 dark:text-indigo-300" />
                {t("reports.unassigned.unassigned_children")}
              </span>
            </h2>
            <p
              className={`mt-2 text-4xl font-bold ${
                unassignedChildren.length > 0
                  ? "text-red-600 dark:text-red-400"
                  : "text-indigo-600 dark:text-indigo-300"
              }`}
            >
              {unassignedChildren.length}
            </p>
            <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
              {t("reports.unassigned.summary")}
            </p>
          </div>
        )}

        {canSeeWaitingWithClass && (
          <div
            className="group cursor-pointer rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:border-indigo-200 hover:shadow-lg dark:border-white/10 dark:bg-slate-900/80 dark:hover:border-indigo-400/30"
            onClick={() => navigate("/reports/waiting-with-class")}
          >
            <h2 className="text-lg font-bold text-slate-950 dark:text-white">
              <span className="inline-flex items-center gap-2">
                <FaClipboardList className="text-2xl text-indigo-600 transition duration-200 group-hover:scale-110 dark:text-indigo-300" />
                {t("reports.waiting_with_class.name")}
              </span>
            </h2>
            <p className="mt-2 text-4xl font-bold text-indigo-600 dark:text-indigo-300">
              {waitingWithClassCount}
            </p>
            <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
              {t("reports.waiting_with_class.summary")}
            </p>
          </div>
        )}

        {/* Alumni preferences */}
        {canViewAlumni && (
          <div
            className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-slate-900/80 cursor-pointer hover:-translate-y-0.5 hover:shadow-[0_25px_50px_rgba(15,23,42,0.15)] dark:hover:shadow-[0_25px_50px_rgba(0,0,0,0.6)] transition"
            onClick={() => navigate("/reports/alumni")}
          >
            <h2 className="text-lg font-bold text-slate-950 dark:text-white">
              <span className="inline-flex items-center gap-2">
                <FaFileExport className="text-2xl text-indigo-600 dark:text-indigo-300" />
                {t("reports.alumni.card_title", { defaultValue: "Alumni preferences" })}
              </span>
            </h2>
            <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
              {t("reports.alumni.card_subtitle", {
                defaultValue: "View saved alumni/contact preferences and export for email outreach.",
              })}
            </p>
            <div className="mt-3 text-3xl font-bold text-indigo-700 dark:text-indigo-200">
              {alumniPrefs.length}
            </div>
            <p className="text-xs font-bold uppercase tracking-[0.08em] text-slate-500 dark:text-slate-400">
              {t("reports.alumni.total_records", { defaultValue: "Total records" })}
            </p>
            <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
              {t("reports.alumni.parents_stored", { defaultValue: "Parents stored" })}:{" "}
              {alumniPrefs.filter((p) => p?.parents?.allowStore || p?.parentPreferences?.allowStore).length}
            </p>
            <button
              type="button"
              onClick={handleExportAlumni}
              disabled={alumniLoading || alumniPrefs.length === 0}
              className={`mt-4 inline-flex items-center gap-2 rounded-full bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow transition hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:ring-offset-2 dark:bg-indigo-500 dark:hover:bg-indigo-400 dark:focus:ring-offset-gray-900 ${
                alumniLoading || alumniPrefs.length === 0 ? "opacity-60 cursor-not-allowed" : ""
              }`}
            >
              <FaFileExport />
              {alumniLoading
                ? t("common.exporting", { defaultValue: "Exporting..." })
                : t("reports.alumni.export_csv", { defaultValue: "Export CSV" })}
            </button>
            {alumniError && (
              <p className="mt-2 text-sm text-red-600 dark:text-red-300">{alumniError}</p>
            )}
            {alumniExpanded && (
              <div className="mt-4 overflow-x-auto">
                {alumniPrefs.length === 0 ? (
                  <p className="text-sm text-slate-600 dark:text-slate-300">
                    {t("reports.alumni.no_records", { defaultValue: "No alumni preferences found." })}
                  </p>
                ) : (
                  <table className="min-w-full text-sm text-left text-gray-800 dark:text-gray-100">
                    <thead>
                      <tr className="border-b border-gray-200 dark:border-gray-700">
                        <th className="py-2 pr-4 font-semibold">
                          {t("reports.alumni.child", { defaultValue: "Child" })}
                        </th>
                        <th className="py-2 pr-4 font-semibold">
                          {t("reports.alumni.parent_contact", { defaultValue: "Parent contact" })}
                        </th>
                        <th className="py-2 pr-4 font-semibold">
                          {t("reports.alumni.parents_allowed", { defaultValue: "Parents stored?" })}
                        </th>
                        <th className="py-2 pr-4 font-semibold">
                          {t("reports.alumni.newsletter", { defaultValue: "Newsletter" })}
                        </th>
                        <th className="py-2 pr-4 font-semibold">
                          {t("reports.alumni.alumni_updates", { defaultValue: "Alumni updates" })}
                        </th>
                        <th className="py-2 pr-4 font-semibold">
                          {t("reports.alumni.activities", { defaultValue: "Activities" })}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {alumniPrefs.map((item, idx) => {
                        const childName =
                          [item?.child?.name, item?.child?.fullName, item?.childName]
                            .filter(Boolean)
                            .join(" ")
                            .trim() || item?.childId || "-";
                        const parentName =
                          [item?.parent?.name, item?.parentName, item?.parent?.fullName]
                            .filter(Boolean)
                            .join(" ")
                            .trim() || "-";
                        const parentEmail = item?.parent?.email || item?.parentEmail || "-";
                        const parentPrefs = item?.parents || item?.parentPreferences || {};
                        return (
                          <tr key={`alumni-${idx}`} className="border-b border-gray-100 dark:border-gray-800">
                            <td className="py-2 pr-4">{childName}</td>
                            <td className="py-2 pr-4">
                              {parentName}
                              {parentEmail && parentEmail !== "-" ? ` (${parentEmail})` : ""}
                            </td>
                            <td className="py-2 pr-4">{yesNo(parentPrefs?.allowStore)}</td>
                            <td className="py-2 pr-4">{yesNo(parentPrefs?.newsletter)}</td>
                            <td className="py-2 pr-4">{yesNo(parentPrefs?.alumni)}</td>
                            <td className="py-2 pr-4">{yesNo(parentPrefs?.activities)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>
        )}
            </LdsOverviewGrid>
    </LdsOverviewPage>
  );
};

export default Reports;
